/* eslint-disable lit/no-invalid-html, lit/binding-positions */
import { nothing, PropertyValueMap, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { html, literal } from 'lit/static-html.js';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BaseElementWithGlobalStyles, customElement } from '../../base-element';
import styles from './icon-button.scss?inline';
import globalStyles from './icon-button.vars.scss?inline';

import type { Icons } from '../../icon/icon.types';

// auto-import the Icon component if not yet pulled in
import '../../icon/icon.wc';

/**
 * A boolean that is allowed to be ABSENT.
 *
 * Lit's default boolean converter cannot express three states: `toAttribute`
 * is `value ? '' : null`, so `false` and `undefined` both remove the attribute,
 * and `fromAttribute` is `value !== null`, so `expanded="false"` in plain HTML
 * reads back as `true`. For `aria-expanded` / `aria-pressed` that erases the
 * distinction the property exists for — "collapsed" is a state that must be
 * announced, "controls nothing" must render no attribute at all. Consumers
 * going through `@lit/react` set properties and never saw it; consumers writing
 * markup did.
 */
const triStateBoolean = {
  toAttribute: (value: boolean | undefined) =>
    value === undefined ? null : String(value),
  fromAttribute: (value: string | null) =>
    value === null ? undefined : value !== 'false',
};

@customElement('ps-icon-button')
export class IconButtonWC extends BaseElementWithGlobalStyles {
  static styles = unsafeCSS(styles);

  static globalStyles = unsafeCSS(globalStyles);

  /** The button's size. */
  @property({ reflect: true }) size: 'xsmall' | 'small' | 'medium' | 'large' =
    'medium';

  /** The button's variant. */
  @property({ reflect: true }) variant:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'new-primary'
    | 'subtle' = 'primary';

  /** Set button shape to round. */
  @property({ reflect: true, type: Boolean }) rounded = false;

  /** Disables the button. */
  @property({ reflect: true, type: Boolean }) disabled = false;

  /** Set button selected state. */
  @property({ reflect: true, type: Boolean }) selected: boolean = false;

  /**
   * The type of button. When the type is `submit`, the button will submit the surrounding form. Note that the default
   * value is `button` instead of `submit`, which is opposite of how native `<button>` elements behave.
   */
  @property() type: 'button' | 'submit' | 'reset' = 'button';

  /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
  @property() href?: string;

  /** Icon name. */
  @property({ reflect: true }) name: keyof typeof Icons;

  /** If button rendered as an `<a>`, it is possible to set target attribute. */
  @property({ reflect: true }) target: HTMLAnchorElement['target'];

  /**
   * Accessible name for the inner `<button>` / `<a>` (`aria-label`).
   *
   * An icon button has NO text, so without this it has no accessible name at
   * all: `<ps-icon>` renders an `<svg>` with no `role`, `aria-label` or
   * `<title>`. And the consumer cannot supply one — the real control lives in
   * this shadow root, so `aria-label` written on the `<ps-icon-button>` tag
   * never reaches it. Mirrors `ps-button`'s property of the same name.
   */
  @property() label?: string;

  /**
   * Whether the thing this button controls is open (`aria-expanded`) — a
   * popover, dropdown, menu, disclosure. `undefined` omits the attribute, so a
   * button that opens nothing is not announced as collapsed.
   */
  @property({ reflect: true, converter: triStateBoolean })
  expanded?: boolean;

  /** What kind of popup this button opens (`aria-haspopup`). */
  @property({ reflect: true, attribute: 'has-popup' }) hasPopup?:
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog'
    | 'true';

  /**
   * Toggle state of a two-state button (`aria-pressed`), and the same visual
   * "selected" paint. `aria-pressed` rather than `role="switch"`: a switch
   * REPLACES the button role and its `aria-checked` is incompatible with
   * `aria-expanded`, which a popup trigger needs.
   *
   * Separate from {@link selected} on purpose: that one is used for "current"
   * as well as for toggles, so mapping it here would change how existing UI
   * announces itself. `selected` stays VISUAL ONLY.
   */
  @property({ reflect: true, converter: triStateBoolean })
  pressed?: boolean;

  firstUpdated(
    _changedProperties: PropertyValueMap<unknown> | Map<PropertyKey, unknown>
  ): void {
    super.firstUpdated(_changedProperties);
    this.emit('load');
  }

  render() {
    const isLink = !!this.href;
    const tag = isLink ? literal`a` : literal`button`;

    let iconSize = ['xsmall'].includes(this.size) ? 'xsmall' : 'small';

    if (this.name === 'meatballs') iconSize = 'medium';

    /* eslint-disable lit/no-invalid-html, lit/binding-positions */
    return html`
      <${tag}
        class=${classMap({
          'c-icon-btn': true,
          [`c-icon-btn--size-${this.size}`]: this.size,
          [`c-icon-btn--${this.variant}`]: this.variant,
          'c-icon-btn--rounded': this.rounded,
          'c-icon-btn--selected': this.selected || this.pressed === true,
        })}
        ?disabled=${ifDefined(isLink ? undefined : this.disabled)}
        type=${ifDefined(isLink ? undefined : this.type)}
        href=${ifDefined(isLink ? this.href : undefined)}
        target=${ifDefined(isLink ? this.target : undefined)}
        aria-label=${ifDefined(this.label || undefined)}
        aria-expanded=${
          this.expanded === undefined ? nothing : String(this.expanded)
        }
        aria-haspopup=${ifDefined(this.hasPopup)}
        aria-pressed=${
          this.pressed === undefined ? nothing : String(this.pressed)
        }

      >
        <span class="c-icon-btn__icon">
          ${
            this.name
              ? html`<ps-icon
                  name=${this.name}
                  size=${iconSize}
                  exportparts="icon: icon-button-icon"
                ></ps-icon>`
              : html`<slot></slot>`
          }
        </span>
      </${tag}>
    `;
    /* eslint-enable lit/no-invalid-html, lit/binding-positions */
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-icon-button': IconButtonWC;
  }
  enum PSElementTagNameMap {
    'ps-icon-button' = 'ps-icon-button',
  }
}
